import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { fmtEUR, fmtEURshort, fmtPct } from "@/lib/format";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

export const PIE_COLORS = ["#8C6D62", "#8AA090", "#D98A72", "#CBA368", "#7F6A93", "#B8928A", "#6B8E8E", "#E8C5C8", "#9C8479", "#A3B5A0"];
const AXIS = { fontSize: 12, fill: "#756B64" };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white border border-[#EAE3DC] shadow-lg px-3 py-2 text-xs min-w-[160px]">
      {label && <div className="font-semibold mb-1 text-[#2C2623]">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center gap-2 py-0.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-[#756B64]">{p.name}</span>
          <span className="ml-auto font-semibold tabular">{fmtEUR(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export const TrendChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={300}>
    <ComposedChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid stroke="#EAE3DC" vertical={false} />
      <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
      <YAxis tickFormatter={fmtEURshort} tick={AXIS} axisLine={false} tickLine={false} width={64} />
      <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F4EFEA" }} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
      <Bar dataKey="incassi" name="Incassi" fill="#8AA090" radius={[6, 6, 0, 0]} maxBarSize={20} />
      <Bar dataKey="costi" name="Costi" fill="#E3A08C" radius={[6, 6, 0, 0]} maxBarSize={20} />
      <Line dataKey="utile" name="Utile" type="monotone" stroke="#B88A4A" strokeWidth={2.5} dot={{ r: 3, fill: "#B88A4A" }} />
    </ComposedChart>
  </ResponsiveContainer>
);

function DonutItem({ d, color, total }) {
  const [open, setOpen] = useState(false);
  const sub = d.dettaglio || [];
  return (
    <li className="text-sm" data-testid={`donut-item-${d.categoria.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className={`flex items-center gap-2 ${sub.length ? "cursor-pointer" : ""}`} onClick={() => sub.length && setOpen((o) => !o)}>
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
        <span className="text-[#5E4A42] truncate">{d.categoria}</span>
        {sub.length > 0 && <ChevronDown className={`h-3.5 w-3.5 text-[#8C6D62] transition-transform ${open ? "rotate-180" : ""}`} />}
        <span className="ml-auto tabular font-medium whitespace-nowrap">{fmtEUR(d.valore)}</span>
        <span className="w-12 text-right text-xs text-[#756B64] tabular">{fmtPct(total ? (d.valore / total) * 100 : 0, 0)}</span>
      </div>
      {open && <ul className="pl-5 mt-1.5 space-y-1 text-xs text-[#756B64]" data-testid="donut-dettaglio">{sub.map((s) => <li key={s.nome} className="flex"><span className="truncate">{s.nome}</span><span className="ml-auto tabular">{fmtEUR(s.valore)}</span><span className="w-12" /></li>)}</ul>}
    </li>
  );
}

export const CostDonut = ({ data }) => {
  const total = data.reduce((s, d) => s + d.valore, 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative w-[220px] h-[220px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="valore" nameKey="categoria" innerRadius={68} outerRadius={104} paddingAngle={2} stroke="none">
              {data.map((d, i) => <Cell key={d.categoria} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none text-center">
          <div>
            <div className="text-[11px] text-[#756B64]">Totale costi</div>
            <div className="text-base font-semibold tabular" data-testid="donut-total">{fmtEUR(total)}</div>
          </div>
        </div>
      </div>
      <ul className="flex-1 w-full space-y-2" data-testid="donut-legend">
        {data.map((d, i) => <DonutItem key={d.categoria} d={d} color={PIE_COLORS[i % PIE_COLORS.length]} total={total} />)}
      </ul>
    </div>
  );
};

export const ChannelChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid stroke="#EAE3DC" vertical={false} />
      <XAxis dataKey="canale" tick={AXIS} axisLine={false} tickLine={false} />
      <YAxis tickFormatter={fmtEURshort} tick={AXIS} axisLine={false} tickLine={false} width={64} />
      <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F4EFEA" }} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
      <Bar dataKey="incassi" name="Incassi" fill="#8AA090" radius={[6, 6, 0, 0]} maxBarSize={28} />
      <Bar dataKey="costi" name="Costi" fill="#E3A08C" radius={[6, 6, 0, 0]} maxBarSize={28} />
      <Bar dataKey="utile" name="Utile" fill="#CBA368" radius={[6, 6, 0, 0]} maxBarSize={28} />
    </BarChart>
  </ResponsiveContainer>
);

export const LineMarginChart = ({ data, labelKey = "linea", marginKey = "margine", marginLabel = "Margine" }) => (
  <ResponsiveContainer width="100%" height={Math.max(200, data.length * 52)}>
    <BarChart data={data} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
      <CartesianGrid stroke="#EAE3DC" horizontal={false} />
      <XAxis type="number" tickFormatter={fmtEURshort} tick={AXIS} axisLine={false} tickLine={false} />
      <YAxis type="category" dataKey={labelKey} tick={AXIS} axisLine={false} tickLine={false} width={labelKey === "marca" ? 120 : 92} />
      <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F4EFEA" }} />
      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="ricavo" name="Ricavo" fill="#E8C5C8" radius={[0, 6, 6, 0]} maxBarSize={16} />
      <Bar dataKey={marginKey} name={marginLabel} fill="#8C6D62" radius={[0, 6, 6, 0]} maxBarSize={16} />
    </BarChart>
  </ResponsiveContainer>
);

export const ProductMarginList = ({ items, tone, testId }) => {
  const max = Math.max(...items.map((i) => Math.abs(i.margine)), 1);
  return (
    <ul className="space-y-4" data-testid={testId}>
      {items.map((p, i) => (
        <li key={p.prodotto_id} data-testid={`${testId}-item-${i}`}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <div className="min-w-0">
              <span className="font-medium text-[#2C2623] truncate block">{p.nome}</span>
              <span className="text-[11px] text-[#756B64]"><b className="font-semibold text-[#5E4A42]">{p.marca}</b> · {p.linea} · {p.quantita} pz · ricavo {fmtEUR(p.ricavo)}</span>
            </div>
            <div className="text-right shrink-0">
              <div className="font-semibold tabular">{fmtEUR(p.margine)}</div>
              <div className="text-[11px] text-[#756B64] tabular">{fmtPct(p.margine_pct)}</div>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-[#F4EFEA] mt-2 overflow-hidden">
            <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(Math.abs(p.margine) / max) * 100}%`, background: tone }} />
          </div>
        </li>
      ))}
    </ul>
  );
};
